import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LoginHeader from './components/LoginHeader';
import LoginForm from './components/LoginForm';
import SecurityNotice from './components/SecurityNotice';

const LoginScreen = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [attemptCount, setAttemptCount] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockTimeRemaining, setBlockTimeRemaining] = useState(0);

  // Mock credentials for demonstration
  const mockCredentials = {
    email: 'admin@vehiclescanner.mx',
    password: 'scanner123'
  };

  useEffect(() => {
    // Check if user is already authenticated
    const token = localStorage.getItem('authToken');
    if (token) {
      navigate('/vehicle-monitoring-dashboard');
    }

    // Check for existing block status
    const blockData = localStorage.getItem('loginBlock');
    if (blockData) {
      const { blockedUntil, attempts } = JSON.parse(blockData);
      const now = Date.now();
      
      if (now < blockedUntil) {
        setIsBlocked(true);
        setAttemptCount(attempts);
        setBlockTimeRemaining(Math.ceil((blockedUntil - now) / 1000));
      } else {
        localStorage.removeItem('loginBlock');
      }
    }
  }, [navigate]);

  useEffect(() => {
    let interval;
    if (isBlocked && blockTimeRemaining > 0) {
      interval = setInterval(() => {
        setBlockTimeRemaining(prev => {
          if (prev <= 1) {
            setIsBlocked(false);
            setAttemptCount(0);
            localStorage.removeItem('loginBlock');
            setError('');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isBlocked, blockTimeRemaining]);

  const handleLogin = async (formData) => {
    if (isBlocked) {
      setError(`Cuenta bloqueada. Intente nuevamente en ${blockTimeRemaining} segundos.`);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Validate credentials
      if (formData?.email === mockCredentials?.email && formData?.password === mockCredentials?.password) {
        // Successful login
        const authToken = `token_${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}`;
        const userData = {
          id: 1,
          email: formData?.email,
          name: 'Administrador del Sistema',
          role: 'admin',
          loginTime: new Date()?.toISOString()
        };

        localStorage.setItem('authToken', authToken);
        localStorage.setItem('userData', JSON.stringify(userData));
        
        if (formData?.rememberMe) {
          localStorage.setItem('rememberLogin', 'true');
        }

        // Clear any existing blocks
        localStorage.removeItem('loginBlock');
        setAttemptCount(0);

        // Redirect to dashboard
        navigate('/vehicle-monitoring-dashboard');
      } else {
        // Failed login
        const newAttemptCount = attemptCount + 1;
        setAttemptCount(newAttemptCount);

        if (newAttemptCount >= 3) {
          // Block account for 5 minutes
          const blockUntil = Date.now() + (5 * 60 * 1000);
          localStorage.setItem('loginBlock', JSON.stringify({
            blockedUntil: blockUntil,
            attempts: newAttemptCount
          }));
          setIsBlocked(true);
          setBlockTimeRemaining(300);
          setError('Cuenta bloqueada por 5 minutos debido a múltiples intentos fallidos.');
        } else {
          setError(`Credenciales incorrectas. ${3 - newAttemptCount} intentos restantes.`);
        }
      }
    } catch (err) {
      setError('Error de conexión. Verifique su conexión a internet e intente nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-card border border-border rounded-xl shadow-card p-8">
          <LoginHeader />
          
          {isBlocked && (
            <div className="mb-6 p-4 bg-warning/10 border border-warning/20 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <div className="w-2 h-2 bg-warning rounded-full animate-pulse-slow"></div>
                <span className="text-sm font-medium text-warning">Cuenta Temporalmente Bloqueada</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Tiempo restante: {Math.floor(blockTimeRemaining / 60)}:{(blockTimeRemaining % 60)?.toString()?.padStart(2, '0')}
              </p>
            </div>
          )}

          <LoginForm 
            onLogin={handleLogin}
            isLoading={isLoading}
            error={error}
          />

          <SecurityNotice />
        </div>

        {/* Development credentials notice */}
        <div className="mt-6 p-4 bg-muted/30 border border-border rounded-lg">
          <p className="text-xs text-muted-foreground text-center mb-2 font-medium">
            Credenciales de Demostración:
          </p>
          <div className="text-xs text-muted-foreground space-y-1 font-mono">
            <p>Email: {mockCredentials?.email}</p>
            <p>Contraseña: {mockCredentials?.password}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;